import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import { FrontendVersion, FrontendVersionSHA } from '../version.constant';

type BackendVersionResponse = {
  backendVersion?: string;
  backendVersionSha?: string;
};

type FrontendVersionResponse = {
  frontendVersion?: string;
  frontendVersionSha?: string;
};

@Injectable({
  providedIn: 'root',
})
export class VersionService {
  private lastSeenBackendVersion?: string;
  private lastSeenFrontendVersion?: string;

  constructor(private readonly http: HttpClient) {}

  get frontendVersion(): string {
    return FrontendVersion;
  }

  get frontendVersionSha(): string {
    return FrontendVersionSHA;
  }

  getBackendVersion() {
    return this.http
      .get<BackendVersionResponse>('/api/version')
      .pipe(first())
      .pipe(
        tap((version) => {
          if (version?.backendVersion) {
            this.lastSeenBackendVersion = version.backendVersion;
          }
        }),
        catchError(() => of({ backendVersion: undefined, backendVersionSha: undefined }))
      );
  }

  getFrontendVersion() {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    });

    return this.http
      .get<FrontendVersionResponse>('/version.json', {
        headers,
        params: { t: Date.now().toString() },
      })
      .pipe(first())
      .pipe(
        tap((version) => {
          if (version?.frontendVersionSha) {
            this.lastSeenFrontendVersion = version.frontendVersionSha;
          } else if (version?.frontendVersion) {
            this.lastSeenFrontendVersion = version.frontendVersion;
          }
        }),
        catchError(() =>
          of({ frontendVersion: undefined, frontendVersionSha: undefined })
        )
      );
  }

  isFrontendUpToDate() {
    return this.getFrontendVersion().pipe(
      map((frontendVersion) => {
        const remoteVersion =
          frontendVersion?.frontendVersionSha || frontendVersion?.frontendVersion;
        if (!remoteVersion) {
          return true;
        }

        return remoteVersion === this.frontendVersionSha;
      })
    );
  }

  get latestBackendVersion(): string | undefined {
    return this.lastSeenBackendVersion;
  }

  get latestFrontendVersion(): string | undefined {
    return this.lastSeenFrontendVersion;
  }
}
